/**
 * Client-side image export utility for light edits
 * Feature: 011-light-editing
 * Tasks: T070-T073
 *
 * Renders light adjustments and crop to a full-resolution canvas,
 * then exports as a downloadable image blob.
 */

import type { Photo } from '../../../types/photo'
import type { CropData, LightEditData, CombinedEditData } from '../../../types/edit.types'
import { exposureToMultiplier, contrastToMultiplier } from './adjustmentConversions'
import logger from '../../../utils/logger'

/**
 * Export options for image rendering
 */
export interface ExportOptions {
  /** JPEG quality (0.0 to 1.0) */
  quality?: number
  /** Output format */
  format?: 'image/jpeg' | 'image/png'
  /** Include EXIF data (if available) */
  preserveExif?: boolean
}

/**
 * Result of image export operation
 */
export interface ExportResult {
  /** Image blob ready for download */
  blob: Blob
  /** Rendered canvas (for debugging) */
  canvas: HTMLCanvasElement
  /** Dimensions of exported image */
  width: number
  height: number
  /** Time taken to render (ms) */
  renderTime: number
}

/**
 * Loads an image from a URL and returns it as an HTMLImageElement
 * @param url - Image URL to load
 * @returns Promise that resolves when image is loaded
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Enable CORS for canvas export

    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image from ${url}`))

    img.src = url
  })
}

/**
 * Gets the full-resolution image URL from a photo's variants
 * Falls back to medium variant if full is not available
 * @param photo - Photo object with variants
 * @returns Full-resolution image URL
 */
function getFullResolutionUrl(photo: Photo): string {
  // Look for full variant first
  const fullVariant = photo.variants.find(v => v.variantType === 'full')
  if (fullVariant) {
    return fullVariant.storageUrl
  }

  // Fall back to medium variant
  const mediumVariant = photo.variants.find(v => v.variantType === 'medium')
  if (mediumVariant) {
    logger.warn('PhotoExport', 'Full-resolution variant not found, using medium variant')
    return mediumVariant.storageUrl
  }

  // Last resort: use first available variant
  logger.warn('PhotoExport', 'No variants found, using first available variant')
  return photo.variants?.[0]?.storageUrl || ''
}

/**
 * Applies light adjustments to a canvas using Canvas 2D API
 * This is a CPU-based implementation for export (not real-time)
 *
 * @param canvas - Canvas to apply adjustments to
 * @param ctx - Canvas 2D context
 * @param lightData - Light adjustment parameters
 */
function applyLightAdjustments(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  lightData: LightEditData
): void {
  // Get image data for pixel manipulation
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  // Convert adjustments to multipliers
  const exposureMultiplier = exposureToMultiplier(lightData.exposure)
  const contrastMultiplier = contrastToMultiplier(lightData.contrast)

  // Normalize highlights/shadows/whites/blacks to 0..1 range
  const highlights = lightData.highlights / 100 // -1..1
  const shadows = lightData.shadows / 100 // -1..1
  const whites = lightData.whites / 100 // -1..1
  const blacks = lightData.blacks / 100 // -1..1

  // Process each pixel
  for (let i = 0; i < data.length; i += 4) {
    let r = data[i] / 255
    let g = data[i + 1] / 255
    let b = data[i + 2] / 255

    // Calculate luminance for selective adjustments
    const luminance = 0.3 * r + 0.3 * g + 0.3 * b

    // Apply exposure (multiplicative)
    r *= exposureMultiplier
    g *= exposureMultiplier
    b *= exposureMultiplier

    // Apply contrast (pivot around 0.5)
    r = 0.5 + contrastMultiplier * (r - 0.5)
    g = 0.5 + contrastMultiplier * (g - 0.5)
    b = 0.5 + contrastMultiplier * (b - 0.5)

    // Apply shadows (brighten dark areas)
    if (shadows !== 0 && luminance < 0.5) {
      const shadowMask = Math.pow(1 - luminance, 2) // Stronger effect in dark areas
      const shadowAdjustment = shadows * shadowMask
      r += shadowAdjustment
      g += shadowAdjustment
      b += shadowAdjustment
    }

    // Apply highlights (darken/brighten bright areas)
    if (highlights !== 0 && luminance > 0.5) {
      const highlightMask = Math.pow(luminance, 2) // Stronger effect in bright areas
      const highlightAdjustment = highlights * highlightMask
      r -= highlightAdjustment
      g -= highlightAdjustment
      b -= highlightAdjustment
    }

    // Apply whites (shift white point)
    if (whites !== 0) {
      const whiteMask = Math.pow(luminance, 3)
      const whiteAdjustment = whites * whiteMask
      r += whiteAdjustment
      g += whiteAdjustment
      b += whiteAdjustment
    }

    // Apply blacks (shift black point)
    if (blacks !== 0) {
      const blackMask = Math.pow(1 - luminance, 3)
      const blackAdjustment = blacks * blackMask
      r -= blackAdjustment
      g -= blackAdjustment
      b -= blackAdjustment
    }

    // Clamp values to 0..1 and convert back to 0..255
    data[i] = Math.max(0, Math.min(255, r * 255))
    data[i + 1] = Math.max(0, Math.min(255, g * 255))
    data[i + 2] = Math.max(0, Math.min(255, b * 255))
    // Alpha channel (data[i + 3]) unchanged
  }

  // Put modified image data back
  ctx.putImageData(imageData, 0, 0)
}

/**
 * Applies crop to a canvas by creating a new cropped canvas
 * @param sourceCanvas - Source canvas to crop
 * @param cropData - Crop parameters (percentages)
 * @returns New canvas with cropped content
 */
function applyCrop(
  sourceCanvas: HTMLCanvasElement,
  cropData: CropData
): HTMLCanvasElement {
  // Convert percentage to pixels
  const sourceWidth = sourceCanvas.width
  const sourceHeight = sourceCanvas.height

  const cropX = (cropData.x / 100) * sourceWidth
  const cropY = (cropData.y / 100) * sourceHeight
  const cropWidth = (cropData.width / 100) * sourceWidth
  const cropHeight = (cropData.height / 100) * sourceHeight

  // Create new canvas with cropped dimensions
  const croppedCanvas = document.createElement('canvas')
  croppedCanvas.width = cropWidth
  croppedCanvas.height = cropHeight

  const ctx = croppedCanvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to get 2D context for crop canvas')
  }

  // Draw cropped region
  ctx.drawImage(
    sourceCanvas,
    cropX, cropY, cropWidth, cropHeight, // Source region
    0, 0, cropWidth, cropHeight // Destination region
  )

  return croppedCanvas
}

/**
 * Exports a photo with light adjustments and optional crop applied
 *
 * @param photo - Photo to export
 * @param lightData - Light adjustments to apply (optional)
 * @param cropData - Crop to apply (optional)
 * @param options - Export options (quality, format, etc.)
 * @returns Promise with export result
 *
 * @example
 * const result = await exportImage(photo, { exposure: 0.5, contrast: 0.2 })
 * const url = URL.createObjectURL(result.blob)
 * downloadLink.href = url
 * downloadLink.download = 'edited-photo.jpg'
 * downloadLink.click()
 */
export async function exportImage(
  photo: Photo,
  lightData?: LightEditData,
  cropData?: CropData,
  options: ExportOptions = {}
): Promise<ExportResult> {
  const startTime = performance.now()

  const {
    quality = 0.95,
    format = 'image/jpeg',
    // preserveExif = true // TODO: EXIF preservation deferred (T074-T076)
  } = options

  // T071: Load full-resolution image
  const imageUrl = getFullResolutionUrl(photo)
  const img = await loadImage(imageUrl)

  // Create canvas with full resolution
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to get 2D context for export canvas')
  }

  // Draw original image
  ctx.drawImage(img, 0, 0)

  // T072: Apply light adjustments if provided
  if (lightData) {
    applyLightAdjustments(canvas, ctx, lightData)
  }

  // T073: Apply crop if provided (creates new canvas)
  let finalCanvas = canvas
  if (cropData) {
    finalCanvas = applyCrop(canvas, cropData)
  }

  // Convert to blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    finalCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to convert canvas to blob'))
        }
      },
      format,
      quality
    )
  })

  const renderTime = performance.now() - startTime

  logger.info('PhotoExport', `Export completed in ${renderTime.toFixed(0)}ms`, {
    originalSize: `${img.naturalWidth}x${img.naturalHeight}`,
    finalSize: `${finalCanvas.width}x${finalCanvas.height}`,
    fileSize: `${(blob.size / 1024 / 1024).toFixed(2)}MB`,
    format,
    quality
  })

  return {
    blob,
    canvas: finalCanvas,
    width: finalCanvas.width,
    height: finalCanvas.height,
    renderTime
  }
}

/**
 * Exports a photo with combined edit (crop + light)
 * @param photo - Photo to export
 * @param combinedData - Combined edit data
 * @param options - Export options
 * @returns Promise with export result
 */
export async function exportCombinedEdit(
  photo: Photo,
  combinedData: CombinedEditData,
  options?: ExportOptions
): Promise<ExportResult> {
  return exportImage(photo, combinedData.light, combinedData.crop, options)
}

/**
 * Creates a download link and triggers browser download
 * @param blob - Image blob to download
 * @param filename - Suggested filename
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  // Clean up object URL after a delay
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
